/**
 * Promo-priced Zazzle SKU mapping.
 *
 * Maps a base product id (matches PRODUCTS[i].id in products.js) to a
 * Zazzle product id at each supported discount level. Each entry is a
 * fully separate Zazzle listing manually cloned in the Designer dashboard
 * at a lower royalty — Zazzle does not expose an API to clone listings or
 * adjust royalty, so this is a one-time manual setup per discount level.
 *
 * Variants per product:
 *   base    — the labeled SKU (matches product.zazzleProductId)
 *   noLabel — the design-only SKU (matches product.noLabelProductId).
 *             Only present for products that ship a separate no-caption
 *             template in Zazzle. Products without a noLabelProductId in
 *             the catalog do not need (and must not have) a noLabel entry
 *             here.
 *
 * The value 'TBD' means "promo SKU has not been created yet". When the
 * resolver encounters TBD it falls back to the base SKU and disables the
 * promo for that product so we never produce a broken Zazzle URL. Replace
 * each TBD with the real cloned product id once created in Zazzle.
 *
 * IMPORTANT: do NOT use a labeled promo SKU when the visitor opted out of
 * the caption (or vice versa) — swapping variants would silently change
 * the design. The resolver enforces this: if the requested variant's
 * promo SKU is missing, it returns null and the caller falls back to the
 * base (full-price) SKU of the SAME variant.
 *
 * To add a new discount level (e.g. 25%):
 *   1. Clone every base SKU (and noLabel SKU where applicable) in Zazzle
 *      at the new royalty.
 *   2. Mark each clone as "Direct only" so it doesn't appear in your store.
 *   3. Add the new percent to SUPPORTED_DISCOUNT_PERCENTS below.
 *   4. Add the new percent key to every PROMO_SKUS entry, preserving the
 *      same variant shape as the existing levels.
 */

export const SUPPORTED_DISCOUNT_PERCENTS = [15];

export const PROMO_SKUS = {
    'cup': { 15: { base: 'https://www.zazzle.com/geoshaped_mug_discount_15-256002232517354378' } },
    'tshirt': { 15: { base: 'https://www.zazzle.com/geoshaped_t_shirt_discount_15-256989803487109084' } },
    'button': { 15: { base: 'https://www.zazzle.com/geoshaped_button_discount_15-256063745393783947', noLabel: 'https://www.zazzle.com/geoshaped_button_no_label_discount_15-256542179312512565' } },
    'stone-magnet': { 15: { base: 'https://www.zazzle.com/geoshaped_stone_magnet_discount_15-256465643983789662', noLabel: 'https://www.zazzle.com/geoshaped_stone_magnet_no_label_discount_15-256465199892777612' } },
    'magnet': { 15: { base: 'https://www.zazzle.com/geoshaped_magnet_discount_15-256855316410437124', noLabel: 'https://www.zazzle.com/geoshaped_magnet_no_label_discount_15-256835904130908802' } },
    'keychain': { 15: { base: 'https://www.zazzle.com/geoshaped_keychain_discount_15-256025203319097798', noLabel: 'https://www.zazzle.com/geoshaped_keychain_no_label_discount_15-256357440191298670' } },
    'pillow': { 15: { base: 'https://www.zazzle.com/geoshaped_pillow_discount_15-256213875333153322' } },
    'stone-coaster': { 15: { base: 'https://www.zazzle.com/geoshaped_stone_coaster_discount_15-256575516289544984' } },
    'beverage-coaster': { 15: { base: 'https://www.zazzle.com/geoshaped_beverage_coaster_discount_15-256275835508344260' } },
    'glass-coaster': { 15: { base: 'https://www.zazzle.com/geoshaped_glass_coaster_discount_15-256825670413955526' } },
    'acrylic-tray': { 15: { base: 'https://www.zazzle.com/geoshaped_acrylic_tray_discount_15-256085265262657457' } },
    'round-clock': { 15: { base: 'https://www.zazzle.com/geoshaped_round_clock_discount_15-256082641990772985' } },
    'water-bottle': { 15: { base: 'https://www.zazzle.com/geoshaped_watter_bottle_discount_15-256400783665718443' } },
    'luggage-tag': { 15: { base: 'https://www.zazzle.com/geoshaped_lurggage_discount_15_luggage-256492995630892717' } },
};

/**
 * Look up the promo Zazzle product id for a given base product +
 * discount level + variant.
 *
 * @param {string} productId       e.g. 'button'
 * @param {number} discountPercent e.g. 15
 * @param {object} [opts]
 * @param {boolean} [opts.noLabel] true → return the no-caption promo SKU
 *
 * Returns null when:
 *   - the discount level isn't configured for this product
 *   - the requested variant ('base' or 'noLabel') isn't configured
 *   - the configured value is still 'TBD'
 *
 * Callers treat null as "no promo SKU available for this exact variant"
 * and fall back to the base (full-price) SKU of the SAME variant — never
 * swap the labeled/no-label choice the visitor made.
 */
export function getPromoProductId(productId, discountPercent, { noLabel = false } = {}) {
    const level = PROMO_SKUS[productId]?.[discountPercent];
    if (!level) return null;
    const variantKey = noLabel ? 'noLabel' : 'base';
    const raw = level[variantKey];
    return extractZazzleProductId(raw);
}

/**
 * Accept either a bare Zazzle product id ("256002232517354378") or a
 * full product URL ("https://www.zazzle.com/geoshaped_mug_discount_15-256002232517354378")
 * and return just the numeric id, which is what the Zazzle Linkover API
 * `pd=` parameter expects.
 *
 * Pasting the full URL is much more ergonomic than hunting for the id in
 * the URL bar, so we let either work. Returns null for missing values,
 * the 'TBD' sentinel, or unparseable strings — the caller treats null as
 * "no promo SKU configured" and falls back to the base price SKU.
 */
function extractZazzleProductId(value) {
    if (!value) return null;
    const s = String(value).trim();
    if (!s || s === 'TBD') return null;
    // Already a bare numeric id.
    if (/^\d+$/.test(s)) return s;
    // Full Zazzle URL — pull the trailing `-<digits>` segment.
    // `-256002232517354378` at the end (optionally followed by a trailing
    // slash) is the id. Query strings and hashes are stripped first so
    // links copied from a deep page still parse.
    const clean = s.split(/[?#]/)[0];
    const match = clean.match(/-(\d+)\/?$/);
    return match ? match[1] : null;
}
